<template>
  <div>
    <v-data-table
      id="dashboard-table"
      ref="dataTable"
      class="
        d-inline overflow-auto
        transparent
      "
      :class="{ 'text-no-wrap' : $vuetify.breakpoint.smAndDown }"
      :headers="config?.dashboardFields"
      :page.sync="pageValue"
      hide-default-footer
      :items="showList"
      :items-per-page="itemsPerPage"
      :headers-length="78"
      locale="fr-FR"
      multi-sort
      :sort-by.sync="tableSortBy"
      :sort-desc.sync="tableSortDesc"
      :custom-sort="items => items"
      :mobile-breakpoint="0"
      item-class=""
    >
      <!-- @click:row="handleRowClick" -->
      <template #top>
        <slot name="top"></slot>
      </template>

      <template v-for="(header, index) in config?.dashboardFields" #[`item.${header.value}`]="{ item }">
        <div
          v-if="item"
          :key="index"
          :class="[
            { 'fill-height' : ['StatusTooltip'].includes(header.component) },
            { 'pointer' : header.sortable }
          ]"
          style="gap: 20px;"
        >
          <component
            :is="header.component"
            v-if="isGenericComponent(header.component)"
            :item="item"
            :header="header"
            :index="index"
            :actions="config.views.list.actions.table"
            :content-type-label="contentTypeLabel"
          />
          <template v-else>
            <UrlContent
              v-if="item.status === 'published' && header.component === 'UrlContent'"
              :item="item"
              :header="header"
            />
            <ConvertOfferToModelOffer
              v-else-if="$store.getters.hasWorkspaceAccess($store.state.centralizedWorkspace, 'WRITE') &&!$store.getters.isCentralizedWorkspace && header.component === 'ConvertOfferToModelOffer'"
              :offer-uuid="item.uuid"
              @set-loading="setLoading"
            />
            <ActionsContent
              v-else-if="header.component === 'ActionsContent'"
              :item="item"
              :actions="config.views.list.actions.table"
              :more-actions="config.views.list.actions.moreActions"
              :content-type-label="contentTypeLabel"
              @set-loading="setLoading"
            />
          </template>
        </div>
      </template>
      <template #[`item.link`]="{ item }">
        <a :href="item.link" target="_blank" @click.stop>{{ item.link }}</a>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';

import { utils } from '@/mixins/index';

import ComputedTextContent from './components/ComputedTextContent.vue';
import DateContent from './components/DateContent.vue';
import NestedComputedTextContent from './components/NestedComputedTextContent.vue';
import StatusTooltip from './components/StatusTooltip.vue';
import TextContent from './components/TextContent.vue';
import SwitchContent from './components/SwitchContent.vue';
import LengthContent from './components/LengthContent.vue';
import UrlContent from './components/UrlContent.vue';
import OfferUrlContent from './components/OfferUrlContent.vue';
import ActionsContent from './components/ActionsContent.vue';
import AppLinkContent from './components/AppLinkContent.vue';
import ConvertOfferToModelOffer from '@/components/offer/actions/ConvertOfferToModelOffer.vue';

export default {
  name: 'Table',
  components: {
    ComputedTextContent,
    DateContent,
    NestedComputedTextContent,
    StatusTooltip,
    TextContent,
    SwitchContent,
    LengthContent,
    UrlContent,
    OfferUrlContent,
    ActionsContent,
    AppLinkContent,
    ConvertOfferToModelOffer
  },
  mixins: [utils],
  props: {
    showList: {
      type: Array,
      default: () => []
    },
    view: {
      type: String,
      default: 'groups'
    },
    clickAction: {
      type: Function,
      default: () => {}
    },
    page: {
      type: Number,
      default: 1
    },
    pageCount: {
      type: Number,
      default: 0
    },
    itemsPerPage: {
      type: Number,
      default: 0
    },
    contentTypeLabel: {
      type: String,
      default: null
    },
    sortConfig: {
      type: Array,
      default: () => []
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['row-click'],
  data () {
    return {
      pageValue: this.page,
      showAllItems: false
    };
  },
  computed: {
    tableSortBy: {
      get () {
        // Converts store format to v-data-table's sortBy array
        return this.sortConfig.map(sortObj => {
          const key = Object.keys(sortObj)[0];
          const field = this.config.dashboardFields.find(f => (f.sortField || f.value) === key);
          return field ? field.value : key;
        });
      },
      set (newSortBy) {
        // Delegate to the central handler
        this.handleSortChange(newSortBy, this.tableSortDesc);
      }
    },
    tableSortDesc: {
      get () {
        // Converts store format to v-data-table's sortDesc array
        return this.sortConfig.map(sortObj => {
          const key = Object.keys(sortObj)[0];
          return sortObj[key].order === 'desc';
        });
      },
      set (newSortDesc) {
        // Delegate to the central handler
        this.handleSortChange(this.tableSortBy, newSortDesc);
      }
    }
  },
  methods: {
    ...mapActions(['setSort']),
    ...mapMutations(['setLoading']),
    isGenericComponent (component) {
      return !(component && ['ActionsContent', 'UrlContent', 'ConvertOfferToModelOffer'].includes(component));
    },
    handleSortChange (sortBy, sortDesc) {
      const newSort = sortBy.map((sortKey, index) => {
        const field = this.config.dashboardFields.find(f => f.value === sortKey);
        const sortField = field?.sortField || sortKey;
        const isDesc = sortDesc[index] || false;
        const order = isDesc ? 'desc' : 'asc';

        if (field && field.sortNestedPath) {
          return {
            [sortField]: {
              order: order,
              nested: { path: field.sortNestedPath }
            }
          };
        }
        return { [sortField]: { order: order } };
      });

      const defaultSortIndex = newSort.findIndex(s => s.lastUpdateTime);
      if (newSort.length > 1 && defaultSortIndex > -1) {
        newSort.splice(defaultSortIndex, 1);
      }

      if (JSON.stringify(newSort) === JSON.stringify(this.sortConfig)) {
        return;
      }

      this.setSort(newSort);
    },
    handleRowClick (item) {
      this.$emit('row-click', item);
    }
  }
};
</script>

<style scoped>
  :deep(tbody tr) {
    height: 84px;
  }
</style>
